import React, { FormEvent, useState } from "react";
import {
  Icon,
  Checkbox,
  CheckboxProps,
  Dropdown,
  Button,
} from "semantic-ui-react";
import { ColumnValue, ColumnLabel } from "../columns/Columns";
import { ProductListAction } from "../ProductList";
import { ChannelLanguagePair } from "../ProductListURLConfig";

export type Props = {
  groupChildren: boolean;
  selectedColumns: ColumnValue[];
  onProductListAction: (
    action: ProductListAction,
    value?:
      | ColumnValue[]
      | boolean
      | {
          [key in
            | "channelLanguagePairs"
            | "referenceChannelLanguagePairs"]: ChannelLanguagePair[];
        }
  ) => void;
  showTextColumns: boolean;
  openChannelLanguageSelectorModal: (open: boolean) => void;
};
export const ColumnSelectorMenuItem: React.FC<Props> = ({
  groupChildren,
  selectedColumns,
  onProductListAction,
  showTextColumns,
  openChannelLanguageSelectorModal,
}) => {
  const [open, setOpen] = useState(false);
  const [checkboxClicked, setCheckBoxClicked] = useState(false);
  const onCheckboxChange = (
    checked: boolean,
    columnValue: ColumnValue
  ): void => {
    if (checked) {
      selectedColumns = [...selectedColumns, columnValue];
    } else {
      selectedColumns = selectedColumns.filter(
        (value: ColumnValue): boolean => value != columnValue
      );
    }
    onProductListAction(ProductListAction.ChangeColumns, selectedColumns);
    setCheckBoxClicked(true);
  };
  const renderDropdownItem = (value: ColumnValue): React.ReactElement => (
    <Dropdown.Item
      onClick={(): void =>
        onCheckboxChange(!selectedColumns.includes(value), value)
      }
    >
      <Checkbox
        style={{ fontSize: "unset" }}
        checked={selectedColumns.includes(value)}
        label={ColumnLabel[value]}
        value={value}
        data-testid={value}
      />
    </Dropdown.Item>
  );

  const handleCommitColumnUpdates = (): void => {
    if (checkboxClicked) {
      onProductListAction(ProductListAction.UpdateColumns);
      setCheckBoxClicked(false);
    }
    setOpen(false);
  };

  const trigger = (
    <span
      style={{ display: "inline-flex", alignItems: "center", gap: "0.35rem" }}
    >
      <Icon name="columns" size="large" />
      <span>Column settings</span>
      <Icon name="dropdown" fitted />
    </span>
  );

  return (
    <Dropdown
      data-testid="column-selector-menu-item"
      icon={false}
      trigger={trigger}
      upward={false}
      floating
      open={open}
      onBlur={handleCommitColumnUpdates}
      onClick={(e: React.SyntheticEvent): void => {
        const dropdownElement = e.currentTarget as HTMLElement;
        const clickedElement = e.nativeEvent.target;
        const dropdownMenu = dropdownElement.querySelector(".menu");
        const clickedInsideOwnMenu = Boolean(
          dropdownMenu &&
            clickedElement instanceof Node &&
            dropdownMenu.contains(clickedElement)
        );

        if (!open) {
          setOpen(true);
          return;
        }

        if (!clickedInsideOwnMenu) {
          handleCommitColumnUpdates();
        }
      }}
      item
    >
      <Dropdown.Menu direction="left">
        <Dropdown.Header content="Select columns" />
        {showTextColumns && (
          <Dropdown.Item>
            <Button
              size="tiny"
              compact
              fluid
              onClick={(): void => {
                openChannelLanguageSelectorModal(true);
              }}
            >
              Select languages(s)...
            </Button>
          </Dropdown.Item>
        )}
        {renderDropdownItem(ColumnValue.SKU)}
        {renderDropdownItem(ColumnValue.EAN)}
        {renderDropdownItem(ColumnValue.ProductType)}
        {renderDropdownItem(ColumnValue.OriginalText)}
        {renderDropdownItem(ColumnValue.TextStatuses)}
        {renderDropdownItem(ColumnValue.Product)}
        {renderDropdownItem(ColumnValue.Status)}
        {renderDropdownItem(ColumnValue.GenerateText)}
        {renderDropdownItem(ColumnValue.Labels)}
        {renderDropdownItem(ColumnValue.Parent)}
        {renderDropdownItem(ColumnValue.Created)}
        {renderDropdownItem(ColumnValue.Updated)}
        <Dropdown.Divider />
        <Dropdown.Item>
          <Checkbox
            style={{ fontSize: "unset" }}
            checked={groupChildren}
            label={ColumnLabel[ColumnValue.GroupChildren]}
            onChange={(
              event: FormEvent<HTMLInputElement>,
              { checked }: CheckboxProps
            ): void => {
              event.preventDefault();
              onProductListAction(
                ProductListAction.ChangeGroupChildren,
                checked
              );
              setCheckBoxClicked(true);
            }}
            value={ColumnValue.GroupChildren}
          />
        </Dropdown.Item>
        <Dropdown.Divider />
        <Dropdown.Item style={{ marginBlock: "0.7rem" }}>
          <Button
            data-testid="column-selector-menu-item-apply-changes"
            fluid
            content="Apply changes"
            basic
            onClick={handleCommitColumnUpdates}
          />
        </Dropdown.Item>
      </Dropdown.Menu>
    </Dropdown>
  );
};
